import { useCallback, useEffect, useMemo, useState } from "react";
import { FiTrash2, FiX } from "react-icons/fi";
import { LuListVideo, LuLock, LuUsers } from "react-icons/lu";
import { toast } from "sonner";
import { AssetsListVisibility } from "@kagami/shared/assetsList";

import Button from "../ui/Button";
import Loader from "../loader";
import ConfirmModal from "./ConfirmModal";
import { Input } from "../ui/shadcn/input";
import { fetchAssetsLists } from "@/components/AssetsList/AssetsListApiClient";
import { useModal } from "@/contexts/ModalContext";
import { useShow } from "@/contexts/ShowContext";
import { useUser } from "@/contexts/UserContext";

export default function AssetsListLoaderModal({ onClose, onDeleteList }) {
  const { show } = useShow();
  const { user } = useUser();
  const { openModal } = useModal();

  const [assetsLists, setAssetsLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const ownerLabel = (assetsList) => {
    if (!assetsList.user) return "Unknown user";
    if (assetsList.user.id === user?.id) return "You";
    return assetsList.user.email || "Unknown user";
  };

  const visibilityIcon = (assetsList) => {
    if (assetsList.visibility === AssetsListVisibility.Public) {
      return <LuUsers className="w-4 h-4" />;
    }
    return <LuLock className="w-4 h-4" />;
  };

  const filteredAssetsLists = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return assetsLists;
    return assetsLists.filter((assetsList) =>
      (assetsList.name || "").toLowerCase().includes(query),
    );
  }, [assetsLists, searchQuery]);

  const refreshAssetsLists = useCallback(async () => {
    if (!show?._id) {
      setLoading(false);
      return;
    }

    try {
      /** @throws AssetsListApiError when the show's lists can't be read */
      const lists = await fetchAssetsLists({ showId: show._id });
      setAssetsLists(lists);
    } catch (error) {
      console.error("Error loading assets lists:", error);
    } finally {
      setLoading(false);
    }
  }, [show?._id]);

  useEffect(() => {
    refreshAssetsLists();
  }, [refreshAssetsLists]);

  const handleDeleteClick = async (assetsList) => {
    const confirmed = await openModal(
      <ConfirmModal
        title="Delete this list?"
        message={`"${assetsList.name || "Untitled list"}" will be deleted for everyone on this show. This cannot be undone.`}
        type="destructive"
      />,
    );

    if (!confirmed) return;

    try {
      /** @throws AssetsListApiError — already toasted by the provider */
      await onDeleteList({ assetsListId: assetsList._id });
      toast.success("List deleted");
    } catch {}

    await refreshAssetsLists();
  };

  return (
    <div
      id="AssetsListLoaderModal"
      className="AssetsListLoaderModal h-full w-full max-w-[600px] max-h-[720px] rounded-md flex flex-col bg-background border gap-4">
      <div className="px-4 pt-4 flex justify-between items-center">
        <h1 className="text-xl font-bold border-l-4 border-primary pl-4">
          Assets lists
        </h1>
        <button
          id="AssetsListLoaderModal__close"
          type="button"
          onClick={() => onClose(null)}
          className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-md text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer"
          title="Close">
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center gap-2 px-4">
        <div className="relative flex-1">
          <Input
            id="AssetsListLoaderModal__search"
            type="text"
            placeholder="Search by name"
            className={` ${searchQuery ? "pr-7" : "pr-2"}`}
            onChange={(event) => setSearchQuery(event.target.value)}
            value={searchQuery}
          />
          {searchQuery && (
            <FiX
              className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer"
              onClick={() => setSearchQuery("")}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col px-2 overflow-y-auto flex-1 relative">
        {!loading && filteredAssetsLists.length === 0 && (
          <div className="py-8 text-center text-sm text-secondary-foreground/60">
            {assetsLists.length === 0
              ? "No saved lists yet."
              : "No lists match your search."}
          </div>
        )}

        {filteredAssetsLists.map((assetsList) => (
          <div
            key={assetsList._id}
            className="AssetsListLoaderModal__row group flex items-center rounded-sm hover:bg-secondary">
            <button
              id={`AssetsListLoaderModal__item-${assetsList._id}`}
              type="button"
              onClick={() => onClose(assetsList)}
              className="AssetsListLoaderModal__item text-left px-3 py-2 flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
              <span className="text-base shrink-0 text-secondary-foreground/70">
                <LuListVideo className="w-4 h-4" />
              </span>
              <span className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-medium truncate">
                  {assetsList.name || "Untitled list"}
                </span>
                <span className="text-[11px] text-secondary-foreground/60 truncate">
                  {assetsList.assetCount} assets · {ownerLabel(assetsList)}
                </span>
              </span>
              <span className="shrink-0 text-secondary-foreground/50">
                {visibilityIcon(assetsList)}
              </span>
            </button>

            <button
              id={`AssetsListLoaderModal__delete-${assetsList._id}`}
              type="button"
              title="Delete list"
              onClick={() => handleDeleteClick(assetsList)}
              className="AssetsListLoaderModal__delete shrink-0 mr-2 p-1.5 rounded-sm text-secondary-foreground/60 hover:text-destructive-foreground hover:bg-background cursor-pointer">
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        {loading && <Loader />}
      </div>

      <div className="flex justify-end p-2 border-t">
        <Button
          id="AssetsListLoaderModal__cancel"
          type="danger-ghost"
          className="min-w-20"
          onClick={() => onClose(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
